import { ExternalLink } from '@color/continuum';
import { useAuthContext } from '@color/lib';
import { Link, useRouteMatch } from 'react-router-dom';

import { MarketingSiteLink } from 'ui/common/components/Links';
import { BASE_COVID_PROXY_PATH, COVID_ROOT_PATH } from 'ui/Covid/paths';

interface Props {
  /**
   * If a URL is given, this component should link to that URL as an external URL.
   *
   * Example: the All of Us client nav, which links to an external participant portal.
   */
  externalLink?: string;
  /**
   * The child of this component is required. It should be a Color logo image.
   */
  children: React.ReactNode;
}

/**
 * This component is intended to wrap an image of the Color logo,
 * with different linking behavior depending on the app/user.
 */
export const LogoLink: React.FC<Props> = ({ externalLink, children }) => {
  const { isAuthenticated } = useAuthContext();
  const isInCovidProxyApp = useRouteMatch({
    path: BASE_COVID_PROXY_PATH,
    strict: false,
  });
  const isInCovidApp = useRouteMatch({
    path: COVID_ROOT_PATH,
    strict: false,
  });

  // TODO (David C): Rieger thinks it's odd that COVID proxies aren't linked to the Color homepage
  // Consider changing this behavior.
  if (isInCovidProxyApp) {
    return <div>{children}</div>;
  }

  if (externalLink) {
    return (
      <ExternalLink linkShouldOpenInCurrentTab to={externalLink}>
        {children}
      </ExternalLink>
    );
  }

  if (isAuthenticated || isInCovidApp) {
    return <Link to="/">{children}</Link>;
  }

  return <MarketingSiteLink>{children}</MarketingSiteLink>;
};
